import React, { useState, useEffect,useRef, useContext, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { MapPin, Star, Loader, ChevronLeft, ChevronRight, Clock, X, Globe, Search, History } from "lucide-react";
import { AuthContext } from "../../../../Context/AuthContext";
import axios from "axios";

// Cache implementation
const searchCache = new Map();
const PROJECT_CACHE_KEY = 'frond_project_';

export default function ActivityList() {
  const { frondId } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectLoading, setProjectLoading] = useState(true);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState("");
  const [filterByInterests, setFilterByInterests] = useState(true);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [filterInfo, setFilterInfo] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [showRecentSearches, setShowRecentSearches] = useState(false);
  const hasSetInitialLocation = useRef(false);


  // Fetch project data to get default location
  const fetchProjectData = useCallback(async () => {
    setProjectLoading(true);
    try {
      const authToken = token || localStorage.getItem("token");
      
      if (!authToken) {
        toast.error("Please login to view activities");
        navigate("/login");
        return;
      }

      // Check cache first
      const cacheKey = `${PROJECT_CACHE_KEY}${frondId}`;
      const cachedProject = localStorage.getItem(cacheKey);
      
      if (cachedProject) {
        const parsedData = JSON.parse(cachedProject);
        setProjectData(parsedData);
        
        // Set location from project if not already set
        if (!location && parsedData?.country) {
          setLocation(parsedData.country);
        }
        return parsedData;
      }

      const response = await axios.get(
        `/frond/${frondId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            Accept: "application/json"
          }
        }
      );
      
      const project = response.data?.response?.data?.frond || 
                     response.data?.response?.data?.project;
      
      if (project) {
        // Cache the project data
        localStorage.setItem(cacheKey, JSON.stringify(project));
        setProjectData(project);
        
        // Set location from project if not already set
        if (!location) {
          // Prefer city if available, otherwise country
          const defaultLocation = project.city || project.country || "Paris";
          setLocation(defaultLocation);
        }
        
        return project;
      }
      
      return null;
    } catch (err) {
      console.error("Error fetching project data:", err);
      toast.error("Could not load project location");
      return null;
    } finally {
      setProjectLoading(false);
    }
  }, [frondId, token]);

  // Fetch activities from API with caching
  const fetchActivities = useCallback(async (searchLocation = location) => {
    setLoading(true);
    setError(null);
    
    try {
      const authToken = token || localStorage.getItem("token");
      
      if (!authToken) {
        toast.error("Please login to view activities");
        navigate("/login");
        return;
      }
      
      // For empty searches, clear activities and show search prompt
      const trimmedLocation = (searchLocation || "").trim();
      if (trimmedLocation.length < 2) {
        // Don't make API call for short/empty searches
        // Just clear the list and show empty state
        setActivities([]);
        setFilterInfo(null);
        setLoading(false);
        return;
      }
      
      // Check cache first
      const cacheKey = `activities_${searchLocation}_${filterByInterests}`;
      const cachedData = searchCache.get(cacheKey);
      
      if (cachedData) {
        // Use cached data
        setActivities(cachedData.activities || []);
        setFilterInfo(cachedData.filter_info || null);
        
        // Add to recent searches if not already there
        addToRecentSearches(searchLocation);
        
        setLoading(false);
        return;
      }
      
      const response = await axios.post(
        "/discover/activities/search",
        {
          location: searchLocation,
          filter_by_interests: filterByInterests
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
            Accept: "application/json"
          }
        }
      );
      
      const data = response.data;
      
      // Cache the response
      searchCache.set(cacheKey, {
        activities: data.activities || [],
        filter_info: data.filter_info || null,
        timestamp: Date.now()
      });
      
      // Clean up old cache entries (older than 1 hour)
      cleanupCache();
      
      setActivities(data.activities || []);
      setFilterInfo(data.filter_info || null);
      
      // Add to recent searches
      addToRecentSearches(searchLocation);
      
    } catch (err) {
      console.error("Error fetching activities:", err);
      
      let errorMessage = "Failed to load activities";
      
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = "Session expired. Please login again.";
          localStorage.removeItem("token");
          navigate("/login");
        } else if (err.response.status === 422) {
          errorMessage = "Invalid location. Please try another city or country.";
        } else if (err.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.request) {
        errorMessage = "Network error. Please check your connection.";
      }
      
      // For empty/cleared input, don't show error - just show empty state
      if (!searchLocation || searchLocation.trim().length < 2) {
        setActivities([]);
        setFilterInfo(null);
      } else {
        setError(errorMessage);
        setActivities([]);
      }
    } finally {
      setLoading(false);
    }
  }, [token, filterByInterests, navigate]);

  // Clean up old cache entries
  const cleanupCache = () => {
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    
    for (const [key, value] of searchCache.entries()) {
      if (now - value.timestamp > oneHour) {
        searchCache.delete(key);
      }
    }
  };

  // Add search to recent searches
  const addToRecentSearches = (searchTerm) => {
    if (!searchTerm?.trim()) return;
    
    const term = searchTerm.trim();
    setRecentSearches(prev => {
      // Remove if already exists
      const filtered = prev.filter(item => item !== term);
      // Add to beginning and limit to 5 items
      const updated = [term, ...filtered].slice(0, 5);
      // Save to localStorage
      localStorage.setItem('recent_activity_searches', JSON.stringify(updated));
      return updated;
    });
  };

  // Load recent searches from localStorage
  useEffect(() => {
    const savedSearches = localStorage.getItem('recent_activity_searches');
    if (savedSearches) {
      try {
        setRecentSearches(JSON.parse(savedSearches));
      } catch (e) {
        console.error("Error loading recent searches:", e);
      }
    }
  }, []);

  // Fetch project data on mount
  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  // Handle search with debounce
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      const trimmedLocation = location.trim();
      
      if (trimmedLocation.length >= 2) {
        // Search for the typed location
        fetchActivities(trimmedLocation);
      }
    }, 500);

    setSearchTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [location, filterByInterests, fetchActivities]);

  const handleBack = () => {
    navigate(`/frond/discover/${frondId}`);
  };

  // Clear location input
  const handleClearLocation = () => {
    setLocation("");
    setShowRecentSearches(true);
  };

  // Handle recent search click
  const handleRecentSearchClick = (searchTerm) => {
    setLocation(searchTerm);
    setShowRecentSearches(false);
    fetchActivities(searchTerm);
  };

  // Remove recent search
  const handleRemoveRecentSearch = (searchTerm, e) => {
    e.stopPropagation();
    setRecentSearches(prev => {
      const updated = prev.filter(item => item !== searchTerm);
      localStorage.setItem('recent_activity_searches', JSON.stringify(updated));
      return updated;
    });
  };

  // Clear all recent searches
  const handleClearAllRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recent_activity_searches');
  };

  const scrollContainerRef = React.useRef(null);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Format price
  const formatPrice = (priceData) => {
    if (!priceData) return "Price not available";
    
    if (typeof priceData === 'string') {
      return priceData.replace('$', '€');
    }
    
    if (typeof priceData === 'object' && priceData.amount && priceData.currencyCode) {
      const currency = priceData.currencyCode === 'EUR' ? '€' : priceData.currencyCode;
      const amount = parseFloat(priceData.amount);
      return `${currency}${amount % 1 === 0 ? amount : amount.toFixed(2)}`;
    }
    
    return "Price not available";
  };

  // Get activity image
  const getActivityImage = (activity) => {
    if (activity.originalData?.pictures && activity.originalData.pictures.length > 0) {
      return activity.originalData.pictures[0];
    }
    
    const searchTerm = encodeURIComponent(activity.name || "travel");
    return `https://source.unsplash.com/400x300/?${searchTerm},${encodeURIComponent(location)}`;
  };

  // Render stars
  const renderStars = (rating) => {
    const numericRating = parseFloat(rating) || 0;
    const stars = [];
    const fullStars = Math.floor(numericRating);
    const hasHalfStar = numericRating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Star
            key={i}
            size={14}
            className="fill-yellow-400 text-yellow-400"
          />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <Star
            key={i}
            size={14}
            className="fill-yellow-400 text-yellow-400"
            style={{ clipPath: "inset(0 50% 0 0)" }}
          />
        );
      } else {
        stars.push(
          <Star key={i} size={14} className="text-gray-300" />
        );
      }
    }
    return stars;
  };

  // Safe rating display
  const getRatingDisplay = (rating) => {
    if (!rating) return "N/A";
    const numericRating = parseFloat(rating);
    return isNaN(numericRating) ? "N/A" : numericRating.toFixed(1);
  };

  // Handle activity click
  const handleActivityClick = (activity) => {
    if (activity.originalData?.bookingLink) {
      window.open(activity.originalData.bookingLink, '_blank');
    } else {
      navigate(`/frond/discover/${frondId}/activities/${activity.id}`, {
        state: { activity }
      });
    }
  };

  // Loading state
  if (projectLoading || (loading && activities.length === 0)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
            <MapPin className="absolute inset-0 m-auto text-blue-400 animate-pulse" size={24} />
          </div>
          <span className="text-lg text-gray-600 font-medium">
            {projectLoading ? "Loading project..." : location ? `Searching activities in ${location}...` : "Enter a location to search"}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-white mb-4 hover:bg-white/10 rounded-lg px-3 py-2 transition-colors"
          >
            <ChevronLeft size={20} />
            <span>Back to Discover</span>
          </button>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                <MapPin size={28} className="text-white" />
                Discover Activities {location ? `in ${location}` : ""}
              </h1>
              <p className="text-orange-100 mt-2">
                {projectData ? (
                  <>
                    Based on your trip to <span className="font-semibold">{projectData.city || projectData.country}</span>
                    {projectData.start_date && projectData.end_date && (
                      <span> from {projectData.start_date} to {projectData.end_date}</span>
                    )}
                  </>
                ) : "Find the best activities for your trip"}
              </p>
            </div>
            
            {filterInfo && (
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-white">
                <div className="text-sm font-medium">
                  {filterInfo.filter_applied ? (
                    <span>
                      Showing {filterInfo.filtered_count || activities.length} of {filterInfo.total_available} activities
                      {filterInfo.matchedInterests && ` matching ${filterInfo.matchedInterests.length} interests`}
                    </span>
                  ) : (
                    <span>Showing {filterInfo.total_available} activities</span>
                  )}
                </div>
                {filterInfo.user_interests && filterInfo.user_interests.length > 0 && (
                  <div className="text-xs text-orange-100 mt-1 truncate max-w-[300px]">
                    Your interests: {filterInfo.user_interests.join(", ")}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <div className="relative">
              <input
                type="text"
                placeholder="Search city or country (e.g., Helsinki, Finland)..."
                value={location}
                onChange={(e) => {
                  setLocation(e.target.value);
                  setShowRecentSearches(false); 
                }}
                onFocus={() => location && setShowRecentSearches(true)}
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all pl-12 pr-10"
              />
              <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              
              <button
                onClick={() => {
                  setLocation("");
                  setShowRecentSearches(false);
                  setActivities([]);
                  setFilterInfo(null);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
              >
                <X size={18} />
              </button>
            </div>

            {/* Recent Searches Dropdown */}
            {showRecentSearches && recentSearches.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 z-50 max-h-64 overflow-y-auto">
                <div className="p-3 border-b border-gray-100 flex justify-between items-center">
                  <div className="flex items-center gap-2 text-gray-600">
                    <History size={16} />
                    <span className="text-sm font-medium">Recent Searches</span>
                  </div>
                  <button
                    onClick={handleClearAllRecentSearches}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Clear all
                  </button>
                </div>
                <div className="py-2">
                  {recentSearches.map((searchTerm, index) => (
                    <div
                      key={index}
                      onClick={() => handleRecentSearchClick(searchTerm)}
                      className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <History size={14} className="text-gray-400" />
                        <span className="text-gray-700">{searchTerm}</span>
                      </div>
                      <button
                        onClick={(e) => handleRemoveRecentSearch(searchTerm, e)}
                        className="text-gray-400 hover:text-gray-600 p-1"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-xl border-2 border-gray-200">
            <div className="flex flex-col">
              <label className="text-sm text-gray-700 font-medium">Filter by interests</label>
              {filterInfo?.user_interests && filterInfo.user_interests.length > 0 && (
                <span className="text-xs text-gray-500">
                  {filterInfo.user_interest_count} interests
                </span>
              )}
            </div>
            <button
              onClick={() => {
                setFilterByInterests(!filterByInterests);
                fetchActivities(location);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                filterByInterests ? "bg-amber-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  filterByInterests ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Location Suggestions */}
        {projectData && (
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="text-gray-600 text-sm">Quick search:</span>
            <button
              onClick={() => {
                setLocation(projectData.city);
                fetchActivities(projectData.city);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium hover:bg-blue-200 transition-colors"
            >
              <Globe size={14} />
              {projectData.city}
            </button>
            <button
              onClick={() => {
                setLocation(projectData.country);
                fetchActivities(projectData.country);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium hover:bg-green-200 transition-colors"
            >
              <Globe size={14} />
              {projectData.country}
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && !activities.length && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                <MapPin size={14} className="text-red-500" />
              </div>
              <div>
                <p className="font-medium">{error}</p>
                <p className="text-sm mt-1">Try searching for a different location</p>
              </div>
            </div>
          </div>
        )}

        {/* Activities Grid/List */}
        {activities.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-lg">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search size={32} className="text-orange-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {location ? `No Activities Found in ${location}` : "Search for Activities"}
            </h3>
            <p className="text-gray-600 mb-4">
              {error ? error : "Enter a city or country above to discover activities"}
            </p>
            {projectData && (
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => {
                    setLocation(projectData.city);
                    fetchActivities(projectData.city);
                  }}
                  className="bg-amber-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-amber-600 transition-colors"
                >
                  Search {projectData.city}
                </button>
                <button
                  onClick={() => {
                    setLocation(projectData.country);
                    fetchActivities(projectData.country);
                  }}
                  className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Search {projectData.country}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="relative">
            {/* Navigation Arrows */}
            <button
              onClick={() => scroll("left")}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-colors hidden md:block"
              style={{ marginLeft: "-16px" }}
            >
              <ChevronLeft size={24} className="text-gray-700" />
            </button>
            
            <div
              ref={scrollContainerRef}
              className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide"
              style={{ scrollBehavior: "smooth", padding: "0 8px" }}
            >
              {activities.map((activity, index) => (
                <div
                  key={activity.id || index}
                  className="flex-shrink-0 w-[280px] md:w-[320px] bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                  onClick={() => handleActivityClick(activity)}
                >
                  {/* Activity Image */}
                  <div className="relative h-40 md:h-48">
                    <img
                      src={getActivityImage(activity)}
                      alt={activity.name || "Activity"}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = `https://source.unsplash.com/400x300/?travel,${encodeURIComponent(location)}`;
                      }}
                    />
                    
                    {/* Recommendation Badge */}
                    {activity.isRecommended && (
                      <div className="absolute top-3 left-3 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-lg flex items-center gap-1">
                        <Star size={10} className="fill-white" />
                        Recommended
                      </div>
                    )}
                    
                    {/* Match Explanation Badge */}
                    {activity.matchExplanation && (
                      <div className="absolute bottom-3 left-3 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                        {activity.matchExplanation}
                      </div>
                    )}
                  </div>

                  {/* Activity Details */}
                  <div className="p-4 flex flex-col h-[calc(100%-12rem)]">
                    <h3 className="font-semibold text-gray-900 text-lg mb-2 line-clamp-2">
                      {activity.name || "Activity Name"}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {activity.description || 
                       activity.originalData?.description?.replace(/<[^>]*>/g, '').substring(0, 100) || 
                       "No description available"}
                    </p>

                    {/* Duration */}
                    {activity.duration && (
                      <div className="flex items-center gap-2 text-gray-500 mb-3">
                        <Clock size={14} />
                        <span className="text-sm">{activity.duration}</span>
                      </div>
                    )}

                    {/* Matched Interests */}
                    {activity.matchedInterests && activity.matchedInterests.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {activity.matchedInterests.map((interest, idx) => (
                          <span 
                            key={idx}
                            className="inline-block px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full"
                          >
                            {interest}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Rating and Price - Fixed at Bottom */}
                    <div className="mt-auto pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {activity.rating && (
                            <>
                              <div className="flex items-center gap-1">
                                {renderStars(activity.rating)}
                              </div>
                              <span className="text-sm font-medium text-gray-700">
                                {getRatingDisplay(activity.rating)}
                              </span>
                            </>
                          )}
                        </div>
                        
                        <div className="text-right">
                          <div className="text-lg font-bold text-gray-900">
                            {formatPrice(activity.price || activity.originalData?.price)}
                          </div>
                          <div className="text-xs text-gray-500">
                            per person
                          </div>
                        </div>
                      </div>

                      {/* View Details Button */}
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleActivityClick(activity);
                        }}
                        className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                      >
                        <span>View Details & Booking</span>
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => scroll("right")}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-colors hidden md:block"
              style={{ marginRight: "-16px" }}
            >
              <ChevronRight size={24} className="text-gray-700" />
            </button>
          </div>
        )}

        {/* Summary Stats */}
        {filterInfo && activities.length > 0 && (
          <div className="mt-8 bg-white rounded-xl p-4 shadow">
            <div className="flex flex-wrap justify-between items-center mb-4">
              <h4 className="text-lg font-semibold text-gray-900">Search Summary</h4>
              <div className="text-sm text-gray-500">
                Location: <span className="font-medium text-gray-700">{location}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {filterInfo.total_available || 0}
                </div>
                <div className="text-sm text-gray-600">Total Activities</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {filterInfo.recommended_count || 0}
                </div>
                <div className="text-sm text-gray-600">Recommended</div>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <div className="text-2xl font-bold text-amber-600">
                  {filterInfo.filtered_count || 0}
                </div>
                <div className="text-sm text-gray-600">Filtered Results</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">
                  {filterInfo.user_interest_count || 0}
                </div>
                <div className="text-sm text-gray-600">Your Interests</div>
              </div>
            </div>
            <div className="mt-4 text-xs text-gray-500 text-center">
              Search results are cached for 1 hour. Clear your cache to get fresh results.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}